import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridRowParams } from '@mui/x-data-grid';

import Confirm from '../../../components/confirm';
import ConfirmInput from '../../../components/confirm-input';
import ConfirmSelect from '../../../components/confirm-select';
import CustomDataGrid from '../../../components/data-grid';
import DisplayAddressResult from '../../../components/display-address-result';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  addressValidation, apiReponseError, apiRequestError, canManualOrderAdmin,
  canManualOrderSubmitter, compareArrayObjectValue, filterCountryStates, formatDateForInput,
  formatDateTimeForDataGrid, getDefaultAddressResult, getErrorObject, goToRoutePage,
  isFormDataValid, isFormReferenceValid, isNullOrWhiteSpace, isValidJSONString,
  openExternalLinkWithTarget, orderContractOrderDetail, orderDetail, orderFolderSearch,
  orderProgramContractOrders, redirectRoutePage, validateEmail, validatePhone
} from '../../../utils/helper';
import {
  AddIcon, AddtionalItemsIcon, ClearAllIcon, DeleteIcon, HistoryIcon, LookUpArrowIcon, MoreIcon,
  OpenInNewIcon, RecentlyUsedTagIcon, ShortcutIcon, ValidateAddressIcon, ValidateEmailIcon,
  ValidatePhoneIcon
} from '../../../utils/icons';
import {
  Alert, Box, Breadcrumbs, Button, FormControl, FormHelperText, Grid, IconButton, Input,
  InputAdornment, InputLabel, LinearProgress, Link, List, ListItem, ListItemIcon, ListItemText,
  Menu, MenuItem, Select, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

const defaultConfirmationDialogSetting = {
  title: "",
  content: "",
  yesText: "",
  noText: "CANCEL",
  open: false,
  callbackOnClose: () => { },
  callbackOnConfirm: (confirm: boolean) => { },
};

const defaultDataValidationStatus: DataValidationStatus = { inProgress: false, valid: false, data: "", executed: false };

const defaultAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const formDefaultValue: NewOrder = {
  folder: {
    clientAccountNumber: "",
    folderId: 0,
    name: "",
    programCode: 0,
    programName: "",
  },
  order: {
    contractOrderId: 0,
    deviceOwnershipId: -1,
    chargeableTypeId: -1,
    orderTypeId: -1,
    orderNodeId: 0,
    crmCaseNumber: "",
    orderId: "",
    shortOrderId: "",
    lastModifiedBy: "",
    lastModifiedDateTime: "",
    status: "",
    statusId: 0,
    manualOrderApprovedBy: "",
    manualOrderApprovedDateTime: "",
    overrideShippingAddressValidation: false,
    fulfillmentVendorId: 0,
    platformVendorId: 0,
    installOption: "",
    processedMessage: "",
    orderSubmittedDate: "",
    overrideSidewalkValidation: false,
    sidewalkValidationResult: false,
    hasSidewalk: false
  },
  shipping: {
    address: {
      addressline1: "",
      addressline2: "",
      addressline3: "",
      country: "USA",
      locality1: "",
      locality2: "",
      postalCode: "",
      region1: "",
      region2: "",
    },
    email: "",
    firstName: "",
    lastName: "",
    organizationName: "",
    phone: "",
    phoneExtension: "",
  },
};

const formDefaultTouchValue = {
  folder: {
    clientAccountNumber: false,
    folderId: false,
    name: false,
    programCode: false,
    programName: false,
  },
  order: {
    contractOrderId: true,
    deviceOwnershipId: true,
    chargeableTypeId: true,
    orderTypeId: true,
    crmCaseNumber: false,
    fulfillmentVendorId: true,
    platformVendorId: true
  },
  shipping: {
    address: {
      addressline1: true,
      addressline2: false,
      addressline3: false,
      country: true,
      locality1: true,
      postalCode: true,
      region1: true,
      region2: false,
    },
    email: true,
    firstName: true,
    lastName: true,
    organizationName: true,
    phone: true,
    phoneExtension: false,
  },
};

const formValidationSchema = Yup.object().shape({
  folder: Yup.object().shape({
    folderId: Yup.number().required("Folder id is required").moreThan(0, "Folder id is required"),
  }),
  order: Yup.object().shape({
    contractOrderId: Yup.number().required("Contract order id is required").moreThan(-1, "Contract order id is required"),
    deviceOwnershipId: Yup.number().required("Device ownership is required").moreThan(0, "Device ownership is required"),
    chargeableTypeId: Yup.number().required("Chargeable type is required").moreThan(-1, "Chargeable type is required"),
    orderTypeId: Yup.number().required("Order type is required").moreThan(-1, "Order type is required"),
    fulfillmentVendorId: Yup.number().required("Fulfillment vendor is required").moreThan(0, "Fulfillment vendor is required"),
    platformVendorId: Yup.number().required("Platform Vendor is required").moreThan(0, "Platform Vendor is required"),
    installOption: Yup.string().trim().required("Install option is required"),
  }),
  shipping: Yup.object().shape({
    address: Yup.object().shape({
      addressline1: Yup.string().trim().required("Address line 1 is required"),
      locality1: Yup.string().trim().required("City is required"),
      region1: Yup.string().trim().required("State is required"),
      postalCode: Yup.string().trim().required("Postal code is required"),
      country: Yup.string().trim().required("Country is required"),
    }),
    organizationName: Yup.string().trim().required("Organization name is required"),
    firstName: Yup.string().trim().required("First name is required"),
    lastName: Yup.string().trim().required("Last name is required"),
    phone: Yup.string().trim().required("Phone number is required").matches(/^\d+$/, "Phone number is not valid").min(10, "Phone number is not valid").max(10, "Phone number is not valid"),
    email: Yup.string().trim().email("Email is not valid").required("Email is required"),
  }),
});

const initialAddressResultOutput: AddressResultOutput = {
  valid: false,
  message: "Something was wrong in our system",
  address: {
    addressline1: "",
    addressline2: "",
    addressline3: "",
    country: "",
    locality1: "",
    locality2: "",
    postalCode: "",
    postalCodeFull: "",
    region1: "",
    region2: "",
  },
  addressLine: "",
  cityStatePostalCountry: "",
  errorCodeTagged: false,
  latitude: 0,
  longitude: 0,
  poBox: false,
  autoCorrect: false,
  sameAddressLine: false,
  addressType: "",
  show: false,
  skip: false,
};

const defaultOrderItem = { id: 0, sku: "", name: "", fulfillable: true, quantity: -1, skuType: "" };

const ManualOrderCRUD = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { orderKey } = useParams();

  const formReference = useRef(null) as any;

  const { userFunctionAccess, referenceLookups, showAlert, appSetting } = useContext(AppContext);

  const [addCRMConfirmOpen, setAddCRMConfirmOpen] = useState<boolean>(false);
  const [overrideDelete, setOverrideDelete] = useState<boolean>(false);
  const [addressChanged, setAddressChanged] = useState<boolean>(false);
  const [addressValidationInProgress, setAddressValidationInProgress] = useState<boolean>(false);
  const [addSkuConfirmOpen, setAddSkuConfirmOpen] = useState<boolean>(false);
  const [anchorElForAddSKUMenu, setAnchorElForAddSKUMenu] = useState<null | HTMLElement>(null);
  const [anchorElForOptionsMenu, setanchorElForOptionsMenu] = React.useState<null | HTMLElement>(null);
  const [anchorElForRecentlyUsedSkusMenu, setAnchorElForRecentlyUsedSkusMenu] = useState<null | HTMLElement>(null);
  const [associateDeviceClicked, setAssociateDeviceClicked] = useState<boolean>(false);
  const [chargeableTypes, setChargeableTypes] = useState<Lookup[]>([]);
  const [confirmationDialogSetting, setConfirmationDialogSetting] = useState<ConfirmationDialogProbs>(defaultConfirmationDialogSetting);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [crmCases, setCrmCases] = useState<CrmCase[]>([]);
  const [crmLookupValues, setCRMLookupValues] = useState<Lookup[]>([]);
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [dataValidationStatusForEmail, setDataValidationStatusForEmail] = useState<DataValidationStatus>(defaultDataValidationStatus);
  const [dataValidationStatusForPhone, setDataValidationStatusForPhone] = useState<DataValidationStatus>(defaultDataValidationStatus);
  const [deviceOwnerships, setDeviceOwnerships] = useState<Lookup[]>([]);
  const [folderId, setFolderId] = useState<number>(0);
  const [folderSearchFormVisible, setFolderSearchFormVisible] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<NewOrder>(formDefaultValue);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openDialogForOrderItemEdit, setOpenDialogForOrderItemEdit] = useState<boolean>(false);
  const [orderFolderSearchItems, setOrderFolderSearchItems] = useState<OrderFolderSearchItem[]>([]);
  const [orderFolderSearchText, setOrderFolderSearchText] = useState<string>("");
  const [orderItems, setOrderItems] = useState<OrderSKUItem[]>([]);
  const [orderTypes, setOrderTypes] = useState<Lookup[]>([]);
  const [programKits, setProgramKits] = useState<OrderProgramKit[]>([]);
  const [programSKULookupValues, setProgramSKULookupValues] = useState<LookupWithCategory[]>([]);
  const [programSKUs, setProgramSKUs] = useState<OrderProgramSku[]>([]);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [recentlyUsedSKUs, setRecentlyUsedSKUs] = useState<RecentlyUsedSKU[]>([]);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [selectedOrderItem, setSelectedOrderItem] = useState<OrderSKUItem>(defaultOrderItem);
  const [shippingAddressResultOutput, setShippingAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [states, setStates] = useState<State[]>([]);
  const [statusId, setStatusId] = useState<number>(0);
  const [fulfillmentVendors, setFulfillmentVendors] = useState<Lookup[]>([]);
  const [platformVendors, setPlatformVendors] = useState<Lookup[]>([]);
  const [alertDataWarnings, setAlertDataWarnings] = useState<string[]>([]);
  const [alertDataErrors, setAlertDataErrors] = useState<string[]>([]);
  const [contractOrder, setContractOrder] = useState<ContractOrder | null>(null);

  let confirmationDialogSettingName = "";
  let seletedSKU = "";

  const columns = [
    { field: "folderId", headerName: "Folder#", width: 150 },
    { field: "name", headerName: "Name", width: 350 },
    { field: "programCode", headerName: "Program Code", width: 200 },
    { field: "programName", headerName: "Program Name", width: 350 },
    { field: "clientAccountNumber", headerName: "Account#", width: 250 },
  ];

  const columnsForOrderItems = [
    { field: "sku", headerName: "SKU", width: 200 },
    { field: "quantity", headerName: "Quantity", width: 150, type: "number" },
    { field: "fulfillable", headerName: "fulfillable", width: 150, type: "boolean" },
    { field: "name", headerName: "Name", width: 350 },
    { field: "skuType", headerName: "Type", width: 200 },
  ];

  const getOrderContractOrderDetail = async (contractOrderId) => {
    setContractOrder(null);

    if (contractOrderId > 0) {
      const filterContracts = contracts.filter(x => x.contractOrderId === contractOrderId);
      if (filterContracts.length === 1) {
        setSaveInProgress(true);
        const apiResponse = await orderContractOrderDetail(filterContracts[0].contractOrderKey);
        if (apiResponse.success) {
          setContractOrder(apiResponse.data.contractOrder);
        }
        setSaveInProgress(false);
      }
    }
  }

  const getProgramOrderContractOrders = async (chargeableTypeId) => {
    setContracts([]);

    if (chargeableTypeId > 0) {
      const filterProgram = referenceLookups.programs.filter(x => Number(x.programCode) === Number(formReference.current?.values?.folder?.programCode));
      if (filterProgram.length === 1) {
        formReference.current?.setFieldValue("order.contractOrderId", 0, true);
        setContractOrder(null);
        setSaveInProgress(true);
        const apiResponse = await orderProgramContractOrders(filterProgram[0].programKey, chargeableTypeId);
        if (apiResponse.success) {
          setContracts(apiResponse.data.contracts);
        }
        setSaveInProgress(false);
      }
    }
  }

  const setAlertErrorsAndWarnings = (items: OrderSKUItem[], currentValues: NewOrder) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    const values = currentValues || formReference.current?.values;
    if (!values) return false;

    if (Number(values.order?.contractOrderId) === 0) {
      warnings.push("You have decided that the contract order ID will be assigned manually by the operation/financial team");
    }

    if (validateAllOrderItemsQuantityLessOrEqualToZero(items) || validateAnyOrderItemsOutsideMasterList(items)) {
      errors.push("One or more SKUs are no longer available in the order system. The item has been zeroed out in quantity. Please remove item and make any adjustments as accordingly");
    }

    if (!validateAllOrderItemsAreFulfillable(items)) {
      errors.push("An unfulfillable SKU(s) has been selected. Please select the ‘Add Supporting Items’ button or delete the unfulfillable SKU(s) to proceed further");
    }

    if (items.length <= 0) {
      errors.push("One or more SKUs are required to process this order");
    }

    if (values.order?.orderTypeId === 0 && validateAnyOrderItemsHasAssociations(items)) {
      warnings.push("SKU(s) with an associated device shortcut has been selected. Please select the ‘Add Supporting Items’ if needed");
    }

    if (validateAnyOrderItemsOutsideProgram(items)) {
      warnings.push("SKU(s) outside of the participant’s program has been selected. Please review order details");
    }

    setAlertDataWarnings(warnings);
    setAlertDataErrors(errors);

    return (warnings.length > 0 || errors.length > 0);
  };

  const handleAnchorMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setanchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setanchorElForOptionsMenu(null);
  };

  const handleAnchorElRecentlyUsedSkusMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForRecentlyUsedSkusMenu(event.currentTarget);
  };

  const handleAnchorElOptionsRecentlyUsedSkusClose = () => {
    setAnchorElForRecentlyUsedSkusMenu(null);
  };

  const isCloneOrderButtonVisible = (mode) => {
    if (!dataChanged && isFormReferenceValid(formReference) && !isNew) {
      if (!formReference.current.errors.hasOwnProperty("shipping") && !formReference.current.errors.hasOwnProperty("order")) {
        if (mode === 0 && orderItems.length > 0) {
          return true;
        }
        if (mode === 1 && readonly && orderItems.length > 0) {
          return true;
        }
      }
    }
    return false;
  };

  const isApproveOrderButtonVisible = () => {
    if (canManualOrderAdmin(userFunctionAccess)) {
      if (!dataChanged && isFormReferenceValid(formReference) && !isNew) {
        if (!formReference.current.errors.hasOwnProperty("shipping") && !formReference.current.errors.hasOwnProperty("order")) {
          if (orderItems.length > 0) {
            let validationStatus = true;

            if (validationStatus) {
              validationStatus = validateAllOrderItemsAreFulfillable(orderItems);
            }

            if (validationStatus) {
              validationStatus = !validateAllOrderItemsQuantityLessOrEqualToZero(orderItems);
            }

            if (validationStatus) {
              validationStatus = !validateAnyOrderItemsOutsideMasterList(orderItems);
            }

            if (validationStatus) {
              const hasSidewalk = formReference.current?.values?.order?.hasSidewalk;
              const sidewalkPassed = formReference.current?.values?.order?.sidewalkValidationResult;
              const overrideActive = formReference.current?.values?.order?.overrideSidewalkValidation;

              // If the order does not have Sidewalk items, it automatically passes this check.
              // Otherwise, it must either have passed validation or have an active override.
              if (hasSidewalk === true) {
                if (sidewalkPassed === false && overrideActive === false) {
                  validationStatus = false;
                }
              }
            }

            if (validationStatus) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };

  const isDeleteOrderButtonVisible = () => {
    if (canManualOrderAdmin(userFunctionAccess)) {
      if (isFormReferenceValid(formReference) && !isNew) {
        return true;
      }
    }
    return false;
  };

  const isSaveOrderButtonVisible = () => {
    if (!readonly && dataChanged) {
      if (isFormReferenceValid(formReference)) {
        if (isFormDataValid(formReference)) {
          if (orderItems.length > 0) {
            let validationStatus = true;

            if (validationStatus) {
              validationStatus = validateAllOrderItemsAreFulfillable(orderItems);
            }

            if (validationStatus) {
              validationStatus = !validateAllOrderItemsQuantityLessOrEqualToZero(orderItems);
            }

            if (validationStatus) {
              validationStatus = !validateAnyOrderItemsOutsideMasterList(orderItems);
            }

            if (validationStatus) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };

  const handlefolderSearchTextChange = (e: React.ChangeEvent<any>) => {
    setOrderFolderSearchText(e.target.value);
  };

  const getOrderDetail = async (orderKey: string, folderId: number) => {
    await resetForm();

    setSaveInProgress(true);
    setOrderItems([]);
    setStatusId(0);
    const apiResponse = await orderDetail(orderKey, folderId);

    if (apiResponse.success) {
      const data = apiResponse.data;
      const overrideShippingAddressValidation = data.order.overrideShippingAddressValidation;
      let readonly2: boolean = true;

      setChargeableTypes(apiResponse.data.chargeableTypes);
      setDeviceOwnerships(apiResponse.data.deviceOwnerships);
      setOrderTypes(apiResponse.data.orderTypes);
      setProgramSKUs(apiResponse.data.programSKUs);
      setProgramKits(apiResponse.data.ProgramKits);
      setFulfillmentVendors(apiResponse.data.fulfillmentVendors);
      setPlatformVendors(apiResponse.data.platformVendors);

      setOverrideDelete(false);
      if (folderId === 0) {
        setFolderId(apiResponse.data.order.folderId);
        setIsNew(false);
        setReadonly(true);
        setStatusId(apiResponse.data.order.statusId);

        const orderProgramSkus = apiResponse.data.orderItems as OrderProgramSku[];
        const tempOrderItems: OrderSKUItem[] = [];

        orderProgramSkus.forEach((orderProgramSku, index) => {
          tempOrderItems.push({ id: index + 1, sku: orderProgramSku.sku, fulfillable: orderProgramSku.fulfillable, name: orderProgramSku.name, quantity: orderProgramSku.quantity, skuType: orderProgramSku.skuType });
        });

        resetOrderItemIds(tempOrderItems);

        if (data.order.statusId === 1) {
          setReadonly(false);
          readonly2 = false;
        }
      } else {
        setFolderId(folderId);
        setIsNew(true);
        setReadonly(false);
        readonly2 = false;
      }

      setCrmCases(apiResponse.data.crmCases);
      setContracts(apiResponse.data.contracts);

      if (data.shipping.address.country === "US") {
        data.shipping.address.country = "USA";
      }

      const states = filterCountryStates(referenceLookups.countries, data.shipping.address.country);

      if (states.filter((x) => x.code.toLowerCase() === data.shipping.address.region1.toLowerCase()).length <= 0) {
        data.shipping.address.region1 = "";
      }

      setStates(states);

      data.shipping.phone = cleanPhoneNumber2(data.shipping.phone);

      formReference.current?.setFieldValue("folder", data.folder);
      formReference.current?.setFieldTouched("folder", true, true);
      formReference.current?.setFieldValue("shipping", data.shipping);
      formReference.current?.setFieldTouched("shipping", true, true);
      formReference.current?.setFieldValue("order", data.order);

      getOrderContractOrderDetail(data.order.contractOrderId);
      formReference.current?.validateForm();

      setFolderSearchFormVisible(false);
      setSaveInProgress(false);

      if (!readonly2) {
        setTimeout(async () => {
          try {
            if (isFormReferenceValid(formReference)) {
              checkEmail();
              checkPhone();
              if (!formReference.current.errors.hasOwnProperty("shipping")) {
                if (!overrideShippingAddressValidation) {
                  checkAddress();
                }
              } else if (!formReference.current.errors.shipping.hasOwnProperty("address")) {
                if (!overrideShippingAddressValidation) {
                  checkAddress();
                }
              } else {
                setAddressChanged(true);
              }
            }
          } catch { }
        }, 3000);
      } else {
        setFolderSearchFormVisible(false);
      }

      try {
        setOverrideDelete(false);
        if (apiResponse.data.order.statusId <= 30) {
          if (canManualOrderAdmin(userFunctionAccess)) {
            setOverrideDelete(true);
          }
        }
      } catch { }

    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setSaveInProgress(false);

      if (apiResponse.errorMessage.indexOf("forbidden") < 0 && orderKey !== referenceLookups.newRecordDefaultId) {
        openConfirmationDialog("notfoundandcanceldraftorder");
      } else {
        cancel();
      }
    }
  };

  const handleDataGridClickForSearchList = async (params: GridRowParams, event: any) => {
    const filterFolder = orderFolderSearchItems.filter((orderFolderSearchItem) => orderFolderSearchItem.folderId === params.row.folderId);
    if (filterFolder.length > 0) {
      await getOrderDetail("", filterFolder[0].folderId);
    }
  };

  const folderSearch = async () => {
    setFolderSearchFormVisible(true);
    setOrderFolderSearchItems([]);
    setSaveInProgress(true);
    const apiResponse = await orderFolderSearch(orderFolderSearchText);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setOrderFolderSearchItems(data.orderFolderSearchItems as OrderFolderSearchItem[]);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
    }
    setSaveInProgress(false);
  };

  const getFolderData = () => {
    const rows: any = [];
    orderFolderSearchItems.map((orderFolderSearchItem, index) => rows.push({ id: index, ...orderFolderSearchItem }));
    return rows;
  };

  const handleAnchorElAddSKUMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForAddSKUMenu(event.currentTarget);
  };

  const handleAnchorElAddSKUMenuClose = () => {
    setAnchorElForAddSKUMenu(null);
  };

  const orderTypeChanged = async (kitNo: number) => {
    setOrderItems([]);

    const filterKits = programKits.filter((programKit) => programKit.kitNo === Number(kitNo));

    if (filterKits.length > 0) {
      const tempOrderItems: OrderSKUItem[] = [];

      filterKits[0].skus.forEach((e) => {
        tempOrderItems.push({ id: tempOrderItems.length + 1, sku: e.sku, name: e.name, fulfillable: true, quantity: e.quantity, skuType: e.skuType });
      });

      resetOrderItemIds(tempOrderItems);
      setDataChanged(true);
    }
  };

  const addFromRecentlyUsed = (sku, name) => {
    const tempOrderItems: OrderSKUItem[] = JSON.parse(JSON.stringify(orderItems));

    if (tempOrderItems.filter((orderItem) => orderItem.sku.toLowerCase() === sku.toLowerCase()).length === 0) {
      const filterOrderItems = referenceLookups.skus.filter((item) => item.partNumber.toLowerCase() === sku.toLowerCase());

      if (filterOrderItems.length === 1) {
        tempOrderItems.push({ id: 1, sku: filterOrderItems[0].partNumber, name: filterOrderItems[0].name, fulfillable: filterOrderItems[0].fulfillable, quantity: 1, skuType: filterOrderItems[0].skuType });
      }
    }

    formReference.current?.setFieldValue("order.orderTypeId", 0);

    resetOrderItemIds(tempOrderItems);
    setAssociateDeviceClicked(false);
    setDataChanged(true);

    const newRecentlyUsedSKUs: RecentlyUsedSKU[] = [];

    recentlyUsedSKUs
      .filter((item) => item.sku.toLowerCase() === sku.toLowerCase())
      .forEach((item) => {
        newRecentlyUsedSKUs.push(item);
      });
    recentlyUsedSKUs
      .filter((item) => item.sku.toLowerCase() !== sku.toLowerCase())
      .forEach((item) => {
        newRecentlyUsedSKUs.push(item);
      });

    window.localStorage.setItem("RecentlyUsedSkus", JSON.stringify(newRecentlyUsedSKUs));
    setRecentlyUsedSKUs(newRecentlyUsedSKUs);
  };

  const getRecentlyUsedSKUs = () => {
    const tempRecentlyUsedSKUs: RecentlyUsedSKU[] = [];
    recentlyUsedSKUs.forEach((sku) => {
      if (orderItems.filter((item) => item.sku.toLowerCase().trim() === sku.sku.toLowerCase().trim()).length === 0) {
        tempRecentlyUsedSKUs.push(sku);
      }
    });
    return tempRecentlyUsedSKUs;
  };

  const handleDataGridClickForOrderItems = async (params: GridRowParams, event: any) => {
    if (!readonly) {
      let valid = false;
      const filterOrderItems = orderItems.filter((orderItem) => orderItem.sku === params.row.sku);

      setSelectedOrderItem(filterOrderItems[0]);
      seletedSKU = filterOrderItems[0].sku;

      if (filterOrderItems.length === 1) {
        if (referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase().trim() === filterOrderItems[0].sku.toLowerCase().trim()).length === 1) {
          valid = true;
        }
      }

      if (!valid) {
        openConfirmationDialog("removeinvalidsku");
      } else {
        setOpenDialogForOrderItemEdit(true);
      }
    }
    return false;
  };

  const updateOrderItem = async (confirmOutput: ConfirmOutput) => {
    setOpenDialogForOrderItemEdit(false);
    try {
      if (confirmOutput.status === "save") {
        const newOrderItems = orderItems.map((obj) => (obj.sku === selectedOrderItem.sku ? { ...obj, quantity: Number(confirmOutput.data) } : obj));
        resetOrderItemIds(newOrderItems);
      } else if (confirmOutput.status === "delete") {
        const newOrderItems = orderItems.filter((obj) => obj.sku !== selectedOrderItem.sku);
        resetOrderItemIds(newOrderItems);
      } else if (confirmOutput.status === "remove") {
        const newOrderItems = orderItems.filter((obj) => obj.sku !== seletedSKU);
        resetOrderItemIds(newOrderItems);
      }

      formReference.current?.setFieldValue("order.orderTypeId", 0);
      setAssociateDeviceClicked(false);
      setDataChanged(true);
    } catch (e) { }
  };

  const handleAddSKU = (mode: number) => {
    const lookupValues: LookupWithCategory[] = [];

    if (mode === 0) {
      programSKUs.forEach((programSKU) => {
        const sku = referenceLookups.skus.find((sku) => sku.partNumber.toLowerCase().trim() === programSKU.sku.toLowerCase().trim());
        if (sku !== undefined) {
          if (orderItems.filter((item) => item.sku.toLowerCase().trim() === sku.partNumber.toLowerCase().trim()).length === 0) {
            lookupValues.push({
              value: sku.partNumber,
              name: sku.name + " - " + sku.partNumber,
              checked: false,
              category: sku.category,
            });
          }
        }
      });
    } else if (mode === 1) {
      referenceLookups.skus.forEach((sku) => {
        if (orderItems.filter((item) => item.sku.toLowerCase().trim() === sku.partNumber.toLowerCase().trim()).length === 0) {
          lookupValues.push({
            value: sku.partNumber,
            name: sku.name + " - " + sku.partNumber,
            checked: false,
            category: sku.category,
          });
        }
      });
    }

    setProgramSKULookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setAddSkuConfirmOpen(true);
  };

  const selectedSKUs = (confirmOutput: ConfirmOutput) => {
    setAddSkuConfirmOpen(false);

    if (confirmOutput.status === "select") {
      const tempOrderItems: OrderSKUItem[] = JSON.parse(JSON.stringify(orderItems));
      const tempRecentlyUsedSKUs: RecentlyUsedSKU[] = [];

      confirmOutput.data.forEach((e) => {
        const filterTempOrderItems = tempOrderItems.filter((orderItem) => orderItem.sku.toLowerCase() === e.value.toLowerCase());
        if (filterTempOrderItems.length === 0) {
          const filterOrderItems = referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase() === e.value.toLowerCase());

          if (filterOrderItems.length === 1) {
            tempOrderItems.push({ id: 1, sku: filterOrderItems[0].partNumber, name: filterOrderItems[0].name, fulfillable: filterOrderItems[0].fulfillable, quantity: 1, skuType: filterOrderItems[0].skuType });
            tempRecentlyUsedSKUs.push({ sku: filterOrderItems[0].partNumber, name: filterOrderItems[0].name, category: filterOrderItems[0].category });
          }
        }
      });

      resetOrderItemIds(tempOrderItems);
      formReference.current?.setFieldValue("order.orderTypeId", 0);
      saveRecentlyUsedSKUs(tempRecentlyUsedSKUs);
      setAssociateDeviceClicked(false);
      setDataChanged(true);
    }
  };

  const saveRecentlyUsedSKUs = (items: RecentlyUsedSKU[]) => {
    const tempRecentlyUsedSKUs: RecentlyUsedSKU[] = JSON.parse(JSON.stringify(recentlyUsedSKUs));
    const newRecentlyUsedSKUs: RecentlyUsedSKU[] = [];

    handleAnchorElOptionsRecentlyUsedSkusClose();

    items.forEach((item) => {
      newRecentlyUsedSKUs.push({ sku: item.sku, name: item.name, category: item.category });
    });

    tempRecentlyUsedSKUs.forEach((tempRecentlyUsedSKU) => {
      if (newRecentlyUsedSKUs.length <= 10) {
        const newRecentlyUsedSKU = newRecentlyUsedSKUs.find((newRecentlyUsedSKU) => newRecentlyUsedSKU.sku.toLowerCase() === tempRecentlyUsedSKU.sku.toLowerCase());

        if (newRecentlyUsedSKU === undefined || newRecentlyUsedSKU === null) {
          newRecentlyUsedSKUs.push({ sku: tempRecentlyUsedSKU.sku, name: tempRecentlyUsedSKU.name, category: tempRecentlyUsedSKU.category });
        }
      } else {
        return;
      }
    });

    window.localStorage.setItem("RecentlyUsedSkus", JSON.stringify(newRecentlyUsedSKUs));
    setRecentlyUsedSKUs(newRecentlyUsedSKUs);
  };

  const resetOrderItemIds = (items: OrderSKUItem[]) => {
    let rowId = 1;

    setOrderItems([]);
    items.forEach((item) => {
      item.id = rowId;
      rowId++;
      if (!readonly && referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase().trim() === item.sku.toLowerCase().trim()).length === 0) {
        item.quantity = 0;
        item.skuType = "Not Found";
      }
    });

    setOrderItems(items);
  };

  const showOrderSkusItemQuantity = () => {
    let totalQuantity = 0;
    if (orderItems.length > 0) {
      totalQuantity = orderItems.reduce((quantity, sku) => {
        return Number(sku.quantity) + Number(quantity);
      }, 0);
    }
    return totalQuantity;
  };

  const validateAnyOrderItemsOutsideProgram = (items) => {
    let returnValue = false;
    for (let i = 0; i < items.length; i++) {
      if (programSKUs.filter((programSKU) => programSKU.sku.toLowerCase().trim() === items[i].sku.toLowerCase().trim()).length === 0) {
        returnValue = true;
        break;
      }
    }
    return returnValue;
  };

  const showSidewalkValidationError = () => {
    let returnValue = false;
    let totalQuantity = showOrderSkusItemQuantity();

    if (totalQuantity > 0 
      && formReference.current?.values?.order?.hasSidewalk === true 
      && formReference.current?.values?.order?.sidewalkValidationResult === false 
      && formReference.current?.values?.order?.overrideSidewalkValidation === false)
    {
      returnValue = true;
    }     

    return returnValue;
  };  

  const validateAllOrderItemsAreFulfillable = (items) => {
    if (validateAllOrderItemsQuantityLessOrEqualToZero(items)) {
      return true;
    }

    let returnValue = true;
    for (let i = 0; i < items.length; i++) {
      if (referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase().trim() === items[i].sku.toLowerCase().trim() && sku.fulfillable === true).length === 0) {
        returnValue = false;
        break;
      }
    }
    return returnValue;
  };

  const validateAnyOrderItemsHasAssociations = (items) => {
    let returnValue = false;

    if (associateDeviceClicked) {
      return false;
    }

    if (formReference.current?.values?.order?.orderTypeId === 0) {
      for (let i = 0; i < items.length; i++) {
        if (referenceLookups.skus.filter((sku) => sku.fulfillable === true && sku.partNumber.toLowerCase().trim() === items[i].sku.toLowerCase().trim() && sku.category.toLowerCase() === "*associations").length > 0) {
          returnValue = true;
          break;
        }
      }
    }
    return returnValue;
  };

  const validateAnyOrderItemsOutsideMasterList = (items) => {
    let returnValue = false;
    for (let i = 0; i < items.length; i++) {
      if (referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase().trim() === items[i].sku.toLowerCase().trim()).length === 0) {
        returnValue = true;
        break;
      }
    }
    return returnValue;
  };

  const validateAllOrderItemsQuantityLessOrEqualToZero = (items) => {
    let returnValue = false;
    if (items.filter((item) => item.quantity <= 0).length > 0) {
      returnValue = true;
    }
    return returnValue;
  };

  const getSKUsAssociatedItems = async () => {
    const skuAssociations: { sku: string; quantity: number }[] = [];

    if (orderItems.length <= 0) {
      return;
    }

    setSaveInProgress(true);
    orderItems.forEach((item) => {
      skuAssociations.push({
        sku: item.sku,
        quantity: item.quantity,
      });
    });

    const payload = {
      skuAssociations: skuAssociations,
    };

    try {
      await API.post("/api/orders/skus/associateditems", payload)
        .then(async (response) => {
          try {
            const tempOrderItems: OrderSKUItem[] = [];

            response.data.skus.forEach((sku) => {
              tempOrderItems.push({ id: 1, ...sku });
            });
            resetOrderItemIds(tempOrderItems);
            setAssociateDeviceClicked(true);
            setDataChanged(true);
          } catch (e) { }

          setSaveInProgress(false);
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to get SKU associated items"),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system"),
        severity: "error",
      });
    }
  };

  const selectedCRMCase = (confirmOutput: ConfirmOutput) => {
    if (confirmOutput.status === "select") {
      formReference.current?.setFieldValue("order.crmCaseNumber", confirmOutput.data["value"]);
      formReference.current?.setFieldTouched("order.crmCaseNumber", true, true);
      setTimeout(() => {
        formReference.current?.validateForm();
      }, 1000);
      setDataChanged(true);
    }
    setAddCRMConfirmOpen(false);
  };

  const handleSelectCrmCases = () => {
    const lookupValues: Lookup[] = [];
    for (let i = 0; i < crmCases.length; i++) {
      lookupValues.push({
        name: crmCases[i].caseNumber,
        value: crmCases[i].caseNumber,
        checked: String(formReference.current?.values?.order?.crmCaseNumber).toLowerCase().trim() === crmCases[i].caseNumber.toLowerCase().trim() ? true : false,
      });
    }
    setCRMLookupValues(lookupValues);
    setAddCRMConfirmOpen(true);
  };

  const openCRMUrl = (caseNumber) => {
    const filterCRMCase = crmCases.filter((item) => item.caseNumber === caseNumber);
    if (filterCRMCase.length === 1) {
      openExternalLinkWithTarget(filterCRMCase[0].url, "crmUrl");
    }
  };

  const checkEmail = async () => {
    if (dataValidationStatusForEmail.inProgress) {
      return;
    }

    setDataValidationStatusForEmail({ inProgress: false, valid: false, executed: false, data: "" });
    formReference.current?.validateForm();

    if (formReference.current?.errors?.hasOwnProperty("shipping") && formReference.current?.errors?.shipping?.hasOwnProperty("email")) {
      return;
    }

    setDataValidationStatusForEmail({ inProgress: true, valid: false, executed: false, data: "" });

    const apiResponse = await validateEmail(formReference.current?.values?.shipping?.email);

    if (apiResponse.success) {
      try {
        if (isValidJSONString(apiResponse.data.emailValidationResult)) {
          const emailValidationResult = apiResponse.data.emailValidationResult as EmailValidationResult;
          const score = parseInt(String(emailValidationResult.validationResult.score * 100.0));

          if (!emailValidationResult.valid) {
            setDataValidationStatusForEmail({
              inProgress: false,
              valid: false,
              executed: true,
              data: "Email is " + emailValidationResult.validationResult.verdict.toLowerCase() + " (Score: " + score + "%)",
            });
          } else {
            if (score <= 70) {
              setDataValidationStatusForEmail({
                inProgress: false,
                valid: false,
                executed: true,
                data: "Email is " + emailValidationResult.validationResult.verdict.toLowerCase() + " (Score: " + score + "%)",
              });
            } else {
              setDataValidationStatusForEmail({
                inProgress: false,
                valid: true,
                executed: true,
                data: "Email is " + emailValidationResult.validationResult.verdict.toLowerCase() + " (Score: " + score + "%)",
              });
            }
          }
        }
      } catch (e) {
        setDataValidationStatusForEmail({ inProgress: false, valid: false, executed: true, data: "Unable to test the email" });
      }
    } else {
      setDataValidationStatusForEmail({ inProgress: false, valid: false, executed: true, data: "Unable to test the email" });
    }
  };

  const checkPhone = async () => {
    if (dataValidationStatusForPhone.inProgress) {
      return;
    }

    setDataValidationStatusForPhone({ inProgress: false, valid: false, executed: false, data: "" });
    formReference.current?.validateForm();
    if (formReference.current?.errors?.hasOwnProperty("shipping") && formReference.current?.errors?.shipping?.hasOwnProperty("phone")) {
      return;
    }

    setDataValidationStatusForPhone({ inProgress: true, valid: false, executed: false, data: "" });

    const apiResponse = await validatePhone(formReference.current?.values?.shipping?.phone);
    if (apiResponse.success) {
      try {
        if (isValidJSONString(apiResponse.data.phoneValidationResult)) {
          const output = apiResponse.data.phoneValidationResult.output;

          if (output !== null) {
            const jsonOutput: any = JSON.parse(output);

            if (jsonOutput.carrier.type === null) {
              setDataValidationStatusForPhone({ inProgress: false, valid: true, executed: true, data: "The Phone number is valid" });
            } else {
              setDataValidationStatusForPhone({ inProgress: false, valid: true, executed: true, data: "The Phone number is valid, and it is a " + String(jsonOutput.carrier.type).toLowerCase() + "" });
            }
          } else {
            setDataValidationStatusForPhone({ inProgress: false, valid: false, executed: true, data: "The Phone number is not valid" });
          }
        }
      } catch (e) {
        setDataValidationStatusForPhone({ inProgress: false, valid: false, executed: true, data: "Unable to test the phone" });
      }
    } else {
      setDataValidationStatusForPhone({ inProgress: false, valid: false, executed: true, data: "Unable to test the phone" });
    }
  };

  const cleanPhoneNumber2 = (phone) => {
    try {
      let phoneNumber = String(phone).trim();
      phoneNumber = phoneNumber.replaceAll("(1", "").replaceAll("+1", "");
      phoneNumber = phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(".", "").replaceAll("+", "").replaceAll(" ", "");
      return phoneNumber;
    } catch { }
    return phone;
  };

  const cleanPhoneNumber = () => {
    try {
      let phoneNumber = cleanPhoneNumber2(formReference.current?.values?.shipping?.phone);

      phoneNumber = phoneNumber.replaceAll("(1", "").replaceAll("+1", "");
      phoneNumber = phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(".", "").replaceAll("+", "").replaceAll(" ", "");
      formReference.current?.setFieldValue("shipping.phone", phoneNumber);
      formReference.current?.setFieldTouched("shipping.phone", true, true);
    } catch { }
  };

  const checkAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current?.values?.shipping?.address?.addressline1,
        addressline2: formReference.current?.values?.shipping?.address?.addressline2,
        addressline3: "",
        country: formReference.current?.values?.shipping?.address?.country,
        locality1: formReference.current?.values?.shipping?.address?.locality1,
        locality2: "",
        postalCode: formReference.current?.values?.shipping?.address?.postalCode,
        region1: formReference.current?.values?.shipping?.address?.region1,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
    addressResultOutput.show = false;
    addressResultOutput.skip = false;

    try {
      setAddressValidationInProgress(true);
      setShippingAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;

            if (address.valid && !address.autoCorrect && address.sameAddressLine && !address.autoCorrect) {
              setAddressChanged(false);
            }
          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
      return addressResultOutput;
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system";
      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
      return addressResultOutput;
    }
  };

  const useThisAddress = () => {
    formReference.current?.setFieldValue("shipping.address", shippingAddressResultOutput.address);

    if (shippingAddressResultOutput.address.country === "US") {
      formReference.current?.setFieldValue("shipping.address.country", "USA");
    } else if (shippingAddressResultOutput.address.country === "CA") {
      formReference.current?.setFieldValue("shipping.address.country", "CAN");
    }

    formReference.current?.setFieldValue("order.overrideShippingAddressValidation", false);
    setDataChanged(true);
    setAddressChanged(false);
    closeAddressMessageStatus();
  };

  const overrideValidationAddress = () => {
    formReference.current?.setFieldValue("order.overrideShippingAddressValidation", true);
    setDataChanged(true);
    setAddressChanged(false);
    closeAddressMessageStatus();
  };

  const overrideSidewalk = () => {
    formReference.current?.setFieldValue("order.overrideSidewalkValidation", true);
    setDataChanged(true);
    console.log("overrideSidewalkValidation:" + formReference.current?.values?.order?.overrideSidewalkValidation);
  };

  const undoOverrideSidewalk = () => {
    formReference.current?.setFieldValue("order.overrideSidewalkValidation", false);
    setDataChanged(true);
  };

  const closeAddressMessageStatus = () => {
    const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
    addressResultOutput.show = false;
    setShippingAddressResultOutput({ ...addressResultOutput });
  };

  const handleAddressChanged = () => {
    setAddressChanged(true);
    closeAddressMessageStatus();
    formReference.current?.setFieldValue("order.overrideShippingAddressValidation", false);
  };

  const handleCountryChanged = (event: any) => {
    setStates(filterCountryStates(referenceLookups.countries, event.target.value));
    formReference.current?.setFieldValue("shipping.address.region1", "");
    formReference.current?.setFieldTouched("shipping.address.region1", true, true);
    setTimeout(() => {
      formReference.current?.validateForm();
    }, 100);
  };

  const resetAddressFields = () => {
    formReference.current?.setFieldValue("shipping.address.addressline1", "");
    formReference.current?.setFieldValue("shipping.address.addressline2", "");
    formReference.current?.setFieldValue("shipping.address.locality1", "");
    formReference.current?.setFieldValue("shipping.address.region1", "");
    formReference.current?.setFieldValue("shipping.address.postalCode", "");
    formReference.current?.setFieldTouched("shipping.address.addressline1", true);
    formReference.current?.setFieldTouched("shipping.address.locality1", true);
    formReference.current?.setFieldTouched("shipping.address.region1", true);
    formReference.current?.setFieldTouched("shipping.address.postalCode", true);
    setTimeout(() => {
      formReference.current?.validateForm();
    }, 100);
    setShippingAddressResultOutput(defaultAddressResultOutput);
  };

  const openConfirmationDialog = (name) => {
    const tempConfirmationDialogSetting: ConfirmationDialogProbs = JSON.parse(JSON.stringify(defaultConfirmationDialogSetting));

    confirmationDialogSettingName = name;
    tempConfirmationDialogSetting.title = "CONFIRMATION";
    tempConfirmationDialogSetting.yesText = "CONFIRM";
    tempConfirmationDialogSetting.noText = "CANCEL";
    tempConfirmationDialogSetting.open = true;
    tempConfirmationDialogSetting.callbackOnClose = closeConfirmationDialog;
    tempConfirmationDialogSetting.callbackOnConfirm = resultConfirmationDialog;

    if (name === "canceldraftorder") {
      tempConfirmationDialogSetting.yesText = "DELETE";
      tempConfirmationDialogSetting.content = "Are you sure you want to Delete this draft order?";

      try {
        if (formReference.current?.values?.order?.statusId <= 30) {
          tempConfirmationDialogSetting.content = "Are you sure you want to Delete this order?";
        }
      } catch { }

      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "notfoundandcanceldraftorder") {
      tempConfirmationDialogSetting.yesText = "DELETE";
      tempConfirmationDialogSetting.content = "Order not found.<br/><br/>Are you sure you want to Delete this draft order?";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "saveedraftorder") {
      let content = "";

      if (validateAnyOrderItemsOutsideProgram(orderItems)) {
        content = content + "<u>SKU(s) outside of the participant’s program has been selected. Please review order details.</u><br/><br/>";
      }

      content = content + "Would you like to save this draft order?";

      tempConfirmationDialogSetting.yesText = "SAVE";
      tempConfirmationDialogSetting.content = content;
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "clonedraftorder") {
      tempConfirmationDialogSetting.content = "Are you sure you want to Clone this draft order?";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "approvedraftorder") {
      let content = "";

      if (validateAnyOrderItemsOutsideProgram(orderItems)) {
        content = content + "<u>SKU(s) outside of the participant’s program has been selected. Please review order details.</u><br/><br/>";
      }

      content = content + "Are you sure you want to Approve this draft order?";

      tempConfirmationDialogSetting.yesText = "APPROVE";
      tempConfirmationDialogSetting.content = content;
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "clearcartsku") {
      tempConfirmationDialogSetting.yesText = "CLEAR";
      tempConfirmationDialogSetting.content = "Are you sure you would like to clear your cart?";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    } else if (name === "removeinvalidsku") {
      tempConfirmationDialogSetting.yesText = "DELETE";
      tempConfirmationDialogSetting.content = "SKU (" + seletedSKU + ") are no longer available in the order system.<br/><br/>Are you sure you want to Delete this SKU?";
      setConfirmationDialogSetting(tempConfirmationDialogSetting);
    }
  };

  const resultConfirmationDialog = async (confirm: boolean) => {
    if (confirm) {
      if (confirmationDialogSettingName === "canceldraftorder" || confirmationDialogSettingName === "notfoundandcanceldraftorder") {
        cancelDraftOrder();
      } else if (confirmationDialogSettingName === "saveedraftorder") {
        saveDraftOrder();
      } else if (confirmationDialogSettingName === "clonedraftorder") {
        cloneDraftOrder();
      } else if (confirmationDialogSettingName === "approvedraftorder") {
        approveDraftOrder();
      } else if (confirmationDialogSettingName === "clearcartsku") {
        setOrderItems([]);
      } else if (confirmationDialogSettingName === "removeinvalidsku") {
        await updateOrderItem({ status: "remove", data: null });
      }
    } else {
      if (confirmationDialogSettingName === "notfoundandcanceldraftorder") {
        cancel();
      }
    }

    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogSetting(defaultConfirmationDialogSetting);
    confirmationDialogSettingName = "";
  };

  const cancelDraftOrder = async () => {
    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.delete("/api/orders/" + orderKey + "/cancel")
        .then(async (response) => {
          showAlert({ message: "Successfully cancelled the order", severity: "success" });
          redirectRoutePage("manualorders");
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current?.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save order data"),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system"),
        severity: "error",
      });
    }
  };

  const approveDraftOrder = async () => {
    const payload = {};

    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.post("/api/orders/" + orderKey + "/approve", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully approved order", severity: "success" });
          cancel();
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current?.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save order data"),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system"),
        severity: "error",
      });
    }
  };

  const cloneDraftOrder = async () => {
    const payload = {};

    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.post("/api/orders/" + orderKey + "/clone", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully cloned the order", severity: "success" });
          if (statusId > 1) {
            redirectRoutePage("manualorders/" + response.data.orderKey);
          } else {
            setSaveInProgress(false);
          }
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current?.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save order data"),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system"),
        severity: "error",
      });
    }
  };

  const saveDraftOrder = async () => {
    const payload = {
      order: {
        chargeableTypeId: formReference.current?.values?.order?.chargeableTypeId,
        crmCaseNumber: formReference.current?.values?.order?.crmCaseNumber,
        deviceOwnershipId: formReference.current?.values?.order?.deviceOwnershipId,
        folderId: formReference.current?.values?.folder?.folderId,
        contractOrderId: formReference.current?.values?.order?.contractOrderId,
        orderTypeId: formReference.current?.values?.order?.orderTypeId,
        overrideShippingAddressValidation: formReference.current?.values?.order?.overrideShippingAddressValidation,
        overrideSidewalkValidation: formReference.current?.values?.order?.overrideSidewalkValidation,
        fulfillmentVendorId: formReference.current?.values?.order?.fulfillmentVendorId,
        platformVendorId: formReference.current?.values?.order?.platformVendorId,
        installOption: formReference.current?.values?.order?.installOption
      },
      shipping: formReference.current?.values?.shipping,
      orderItems: [] as { quantity: number; sku: string }[],
    };

    orderItems.forEach((item) => {
      payload.orderItems.push({
        quantity: item.quantity,
        sku: item.sku,
      });
    });

    setSaveInProgress(true);

    try {
      setSaveInProgress(true);
      await API.post("/api/orders/" + orderKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved", severity: "success" });
          redirectRoutePage("manualorders/" + response.data.orderKey);
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current?.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save order data"),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system"),
        severity: "error",
      });
    }
  };

  const validateSaveDraft = async () => {
    formReference.current?.validateForm();

    if (!isFormDataValid(formReference)) {
      showAlert({ message: "One or more fields required", severity: "error" });
      return;
    }

    let validationRequired = false;
    let message = "";

    if (!dataValidationStatusForEmail.executed) {
      message += (message !== "" ? "," : "") + "email";
      checkEmail();
      validationRequired = true;
    }

    if (!dataValidationStatusForPhone.executed) {
      message += (message !== "" ? "," : "") + "phone";
      checkPhone();
      validationRequired = true;
    }

    if (addressChanged) {
      message += (message !== "" ? "," : "") + "address";
      checkAddress();
      setAddressChanged(false);
      validationRequired = true;
    }

    if (validationRequired) {
      message = "The " + message + " has been changed or never validated.\nPlease wait until validation is complete and click the 'Update/Save' button again";
      showAlert({ message: message, severity: "warning" });
      return;
    }

    if (!shippingAddressResultOutput.valid && !formReference.current?.values?.order?.overrideShippingAddressValidation) {
      showAlert({ message: "The shipping address needs to be corrected to proceed with this order", severity: "info" });
      return;
    }

    openConfirmationDialog("saveedraftorder");
  };

  const resetForm = async () => {
    if (formReference !== null) {
      if (formReference.current !== null) {
        formReference.current.resetForm();
      }
    }

    setDataChanged(false);
    setReadonly(true);
    setAddressChanged(false);
    setShippingAddressResultOutput(initialAddressResultOutput);
    setInitialFormValues(formDefaultValue);
    setStates(filterCountryStates(referenceLookups.countries, formDefaultValue.shipping.address.country));
  };

  const cancel = () => {
    if (orderKey === referenceLookups.newRecordDefaultId) {
      setFolderSearchFormVisible(true);
    } else {
      redirectRoutePage("manualorders");
    }
  };

  useEffect(() => {
    const initializePage = async () => {
      if (!canManualOrderSubmitter(userFunctionAccess)) {
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (isNullOrWhiteSpace(orderKey)) {
        showAlert({ message: "Order key missing", severity: "error" });
        goToRoutePage("order");
        return;
      }

      setRecentlyUsedSKUs([]);
      setOrderItems([]);

      if (orderKey === referenceLookups.newRecordDefaultId) {
        setFolderSearchFormVisible(true);
        resetForm();
      } else {
        await getOrderDetail(orderKey, 0);
      }
      setLoading(false);

      try {
        const tempRecentlyUsedSKUsString = window.localStorage.getItem("RecentlyUsedSkus");
        handleAnchorElOptionsRecentlyUsedSkusClose();

        if (tempRecentlyUsedSKUsString !== undefined && tempRecentlyUsedSKUsString !== null && tempRecentlyUsedSKUsString !== "") {
          const tempRecentlyUsedSKUs: RecentlyUsedSKU[] = JSON.parse(tempRecentlyUsedSKUsString as string);
          tempRecentlyUsedSKUs.forEach((item) => {
            const filterOrderItems = referenceLookups.skus.filter((sku) => sku.partNumber.toLowerCase() === item.sku.toLowerCase());

            if (filterOrderItems.length === 1) {
              item.name = filterOrderItems[0].name;
              item.category = filterOrderItems[0].category;
            }
          });

          setRecentlyUsedSKUs(tempRecentlyUsedSKUs);
          window.localStorage.setItem("RecentlyUsedSkus", JSON.stringify(tempRecentlyUsedSKUs));
        }
      } catch { }
    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey]);

  return (
    <React.Fragment>
      <ConfirmSelect
        title="Pick SKU #"
        label={"SKUs"}
        content={``}
        data={programSKULookupValues}
        yesText="SELECT"
        noText="CANCEL"
        open={addSkuConfirmOpen}
        multiSelect={true}
        maxSelect={0}
        callbackOnSelect={selectedSKUs}
      />

      <ConfirmSelect
        title="Pick CRM Case #"
        label={"CRM Case #"}
        content={``}
        data={crmLookupValues}
        yesText="SELECT"
        noText="CANCEL"
        open={addCRMConfirmOpen}
        multiSelect={false}
        maxSelect={1}
        callbackOnSelect={selectedCRMCase}
      />

      <Confirm
        title={confirmationDialogSetting.title}
        content={confirmationDialogSetting.content}
        yesText={confirmationDialogSetting.yesText}
        noText={confirmationDialogSetting.noText}
        open={confirmationDialogSetting.open}
        callbackOnClose={confirmationDialogSetting.callbackOnClose}
        callbackOnConfirm={confirmationDialogSetting.callbackOnConfirm}
      />

      <ConfirmInput
        title={"Edit Item"}
        label="Quantity"
        content={`<h3>${selectedOrderItem.sku} - ${selectedOrderItem.name}</h3>`}
        value={selectedOrderItem.quantity.toString()}
        type="number-pos"
        required={true}
        yesText="UPDATE"
        noText="CANCEL"
        open={openDialogForOrderItemEdit}
        callbackOnSave={updateOrderItem}
        deleteConfirmationText={`${selectedOrderItem.sku} - ${selectedOrderItem.name}<p>Are you sure you want to Delete this SKU?</p>`}
        showDeleteButton={true}
      />

      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />

          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
            >
              <Grid container>
                <Grid
                  item
                  xs={10}
                  sm={10}
                  md={10}
                  lg={10}
                >
                  <Box className={globalStyles.bottomSpacer}>
                    <Breadcrumbs aria-label="breadcrumb">
                      <Link
                        color="inherit"
                        onClick={() => goToRoutePage("home")}
                        className={globalStyles.link}
                      >
                        Home
                      </Link>
                      <Link
                        color="inherit"
                        onClick={() => goToRoutePage("manualorders")}
                        className={globalStyles.link}
                      >
                        Manual Orders
                      </Link>
                      <Typography color="textPrimary">{isNew ? "New" : readonly ? "View" : "Edit"}</Typography>
                    </Breadcrumbs>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sm={2}
                  md={2}
                  lg={2}
                >
                  {folderId > 0 ? (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      width={1}
                    >
                      <Button
                        color="secondary"
                        aria-controls="action-menu"
                        aria-haspopup="true"
                        onClick={handleAnchorMenuClick}
                      >
                        <MoreIcon />
                      </Button>
                      <Menu
                        elevation={2}
                        id="action-menu"
                        className={globalStyles.rightsideMenu}
                        anchorEl={anchorElForOptionsMenu}
                        keepMounted
                        open={Boolean(anchorElForOptionsMenu)}
                        onClose={handleAnchorElOptionsMenuClose}
                      >
                        <MenuItem
                          onClick={() => {
                            openExternalLinkWithTarget(appSetting.meshifyUrl + "/admin#/folders/" + folderId, "meshifyUrl");
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Meshify Admin" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <OpenInNewIcon color="secondary" />
                          </ListItemIcon>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            openExternalLinkWithTarget(appSetting.meshifyUrl + "/dashboard3#folders?folderId=" + folderId, "meshifyUrl");
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Meshify Dashboard" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <OpenInNewIcon color="secondary" />
                          </ListItemIcon>
                        </MenuItem>
                      </Menu>
                    </Box>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              className={globalStyles.formikContainer}
            >
              <Formik
                innerRef={formReference}
                initialValues={initialFormValues}
                validationSchema={!readonly ? formValidationSchema : null}
                validateOnMount={true}
                initialTouched={!readonly ? formDefaultTouchValue : {}}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={() => {
                  validateSaveDraft();
                }}
              >
                {({ errors, handleBlur, handleChange, isSubmitting, resetForm, setFieldTouched, setFieldValue, touched, validateField, validateForm, values }) => (
                  <Form
                    onMouseMove={(e) => { setAlertErrorsAndWarnings(orderItems, values); }}
                    onChange={(e) => {
                      handleChange(e);
                      setDataChanged(true);
                      setAlertErrorsAndWarnings(orderItems, values);
                    }}
                  >
                    <Grid
                      container
                      spacing={3}
                    >
                      {folderSearchFormVisible ? (
                        <Grid
                          item
                          xs={12}
                        >
                          <Grid
                            container
                            spacing={3}
                          >
                            <Grid
                              item
                              xs={8}
                              md={6}
                            >
                              <TextField
                                id="folderSerachText"
                                name="folderSerachText"
                                label="Search (Folder Id or Folder Name or Account Number)"
                                value={orderFolderSearchText}
                                onChange={handlefolderSearchTextChange}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              md={2}
                            >
                              <Box
                                p={1}
                                display="flex"
                                justifyContent="center"
                              >
                                <Button
                                  disabled={orderFolderSearchText.length < 3}
                                  color="secondary"
                                  variant="contained"
                                  onClick={folderSearch}
                                >
                                  {saveInProgress ? "Searching..." : "Search"}
                                </Button>
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                            >
                              <Box className={pageStyles.datagridContainerForFolderSearch}>
                                <CustomDataGrid
                                  uniqueGridCode={"OrderFolderSearchResult"}
                                  columns={columns}
                                  rows={getFolderData()}
                                  showToolBar={true}
                                  sortModel={[{ field: "name", sort: "desc" }]}
                                  handleDataGridClick={handleDataGridClickForSearchList}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                        >
                          <Grid container spacing={3} >
                            {values.order.statusId > 30 ? (
                              <>
                                <Grid item xs={12} >
                                  <Box className={globalStyles.topic}>Order</Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3} lg={2} >
                                  <TextField
                                    disabled={true}
                                    label="Short Order Id"
                                    value={values.order.shortOrderId}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <OpenInNewIcon color="secondary" onClick={() => {
                                            openExternalLinkWithTarget(appSetting.meshifyUrl + "/dashboard3#nodes?folderId=" + values.folder.folderId + "&nodeId=" + values.order.orderNodeId, "meshifyUrl");
                                          }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3} lg={2} >
                                  <TextField
                                    disabled={true}
                                    label="Order Id"
                                    value={values.order.orderId}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3} lg={2} >
                                  <TextField
                                    disabled={true}
                                    label="Processed Date"
                                    value={formatDateTimeForDataGrid(values.order.orderSubmittedDate)}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} lg={6} >
                                  <TextField
                                    disabled={true}
                                    label="Note"
                                    value={values.order.processedMessage}
                                  />
                                </Grid>
                              </>
                            ) : null}

                            <Grid
                              item
                              xs={12}
                              md={6}
                              lg={6}
                            >
                              <Grid
                                container
                                spacing={3}
                              >
                                <Grid
                                  item
                                  xs={12}
                                >
                                  <Box className={globalStyles.topic}>Participant</Box>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={12}
                                >
                                  <TextField
                                    disabled={true}
                                    label="Folder"
                                    value={`${values.folder.folderId} - ${values.folder.name}`}
                                  />
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={12}
                                >
                                  <TextField
                                    disabled={true}
                                    label="Program"
                                    value={`${values.folder.programCode} - ${values.folder.programName}`}
                                  />
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={12}
                                >
                                  <TextField
                                    disabled={true}
                                    label="Account Number"
                                    value={values.folder.clientAccountNumber}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              md={6}
                              lg={6}
                            >
                              <Grid
                                container
                                spacing={3}
                              >
                                <Grid
                                  item
                                  xs={12}
                                >
                                  <Box className={globalStyles.topic}>Audit</Box>
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    disabled={true}
                                    label="Status"
                                    value={values.order.status}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    disabled={true}
                                    label="Last Modified"
                                    value={values.order.lastModifiedBy}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    disabled={true}
                                    label="Last Modified Date"
                                    value={formatDateTimeForDataGrid(values.order.lastModifiedDateTime)}
                                  />
                                </Grid>

                                {values.order.statusId > 30 ? (
                                  <>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        disabled={true}
                                        label="Approved By"
                                        value={values.order.manualOrderApprovedBy}
                                      />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        disabled={true}
                                        label="Approved Date"
                                        value={formatDateTimeForDataGrid(values.order.manualOrderApprovedDateTime)}
                                      />
                                    </Grid>
                                  </>
                                ) : null}
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                            >
                              <Box className={globalStyles.topic}>
                                {addressValidationInProgress || dataValidationStatusForEmail.inProgress || dataValidationStatusForPhone.inProgress ? <LinearProgress color="secondary" /> : null}
                                <Grid container>
                                  <Grid
                                    item
                                    xs={8}
                                  >
                                    Shipping Information
                                  </Grid>
                                  <Grid
                                    item
                                    xs={4}
                                  >
                                    <Box
                                      display="flex"
                                      justifyContent="flex-end"
                                      width={1}
                                    >
                                      {!readonly && !addressValidationInProgress && !dataValidationStatusForEmail.inProgress && !dataValidationStatusForPhone.inProgress ? (
                                        <>
                                          <Tooltip arrow title="Validate email">
                                            <IconButton onClick={checkEmail}>
                                              <ValidateEmailIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                          <Tooltip arrow title="Validate phone number">
                                            <IconButton onClick={checkPhone}>
                                              <ValidatePhoneIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                          <Tooltip arrow title="Validate address">
                                            <IconButton onClick={checkAddress}>
                                              <ValidateAddressIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                          <Tooltip arrow title="Clear address">
                                            <IconButton onClick={resetAddressFields}>
                                              <ClearAllIcon color="error" />
                                            </IconButton>
                                          </Tooltip>
                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                        </>
                                      ) : null}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                            >
                              <Grid
                                container
                                spacing={3}
                              >
                                {shippingAddressResultOutput.show ? (
                                  <Grid
                                    item
                                    xs={12}
                                  >
                                    <DisplayAddressResult
                                      addressResultOutput={shippingAddressResultOutput}
                                      currentAddress={values.shipping.address}
                                      callbackOnClose={closeAddressMessageStatus}
                                      useThisAddressButton={true}
                                      callbackOnUseThisAddress={useThisAddress}
                                      overrideValidationAddress={true}
                                      callbackOnOverrideValidationAddress={overrideValidationAddress}
                                    />
                                  </Grid>
                                ) : null}

                                {values.order.overrideShippingAddressValidation ? (
                                  <Grid
                                    item
                                    xs={12}
                                  >
                                    <Alert severity="info">Shipping address validation is overwritten.</Alert>
                                  </Grid>
                                ) : null}

                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  lg={6}
                                >
                                  <Grid
                                    container
                                    spacing={3}
                                  >
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={8}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.organizationName"
                                        name="shipping.organizationName"
                                        label="Organization Name"
                                        value={values.shipping.organizationName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.organizationName"))}
                                        helperText={getIn(errors, "shipping.organizationName")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.firstName"
                                        name="shipping.firstName"
                                        label="First Name"
                                        value={values.shipping.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.firstName"))}
                                        helperText={getIn(errors, "shipping.firstName")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.lastName"
                                        name="shipping.lastName"
                                        label="Last Name"
                                        value={values.shipping.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.lastName"))}
                                        helperText={getIn(errors, "shipping.lastName")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.email"
                                        name="shipping.email"
                                        label="Email"
                                        value={values.shipping.email}
                                        onChange={(e) => {
                                          handleChange(e);
                                          setDataValidationStatusForEmail(defaultDataValidationStatus);
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.email"))}
                                        helperText={getIn(errors, "shipping.email")}
                                      />
                                      {dataValidationStatusForEmail.executed ? (
                                        <Typography
                                          variant="body2"
                                          className={dataValidationStatusForEmail.valid ? globalStyles.successText : globalStyles.warningText}
                                        >
                                          {dataValidationStatusForEmail.data}
                                        </Typography>
                                      ) : null}
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <Grid
                                        container
                                        spacing={3}
                                      >
                                        <Grid
                                          item
                                          xs={9}
                                        >
                                          <TextField
                                            disabled={readonly}
                                            id="shipping.phone"
                                            name="shipping.phone"
                                            label="Phone"
                                            value={values.shipping.phone}
                                            onChange={(e) => {
                                              handleChange(e);
                                              setTimeout(() => {
                                                cleanPhoneNumber();
                                              }, 1000);
                                              setDataValidationStatusForPhone(defaultDataValidationStatus);
                                            }}
                                            onBlur={handleBlur}
                                            error={Boolean(getIn(errors, "shipping.phone"))}
                                            helperText={getIn(errors, "shipping.phone")}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xs={3}
                                        >
                                          <TextField
                                            disabled={readonly}
                                            id="shipping.phoneExtension"
                                            name="shipping.phoneExtension"
                                            label="Ext"
                                            value={values.shipping.phoneExtension}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </Grid>
                                      </Grid>

                                      {dataValidationStatusForPhone.executed ? (
                                        <Typography
                                          variant="body2"
                                          className={dataValidationStatusForPhone.valid ? globalStyles.successText : globalStyles.warningText}
                                        >
                                          {dataValidationStatusForPhone.data}
                                        </Typography>
                                      ) : null}
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  lg={6}
                                >
                                  <Grid
                                    container
                                    spacing={3}
                                  >
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.address.addressline1"
                                        name="shipping.address.addressline1"
                                        label="Address Line 1"
                                        value={values.shipping.address.addressline1}
                                        onChange={(e) => {
                                          handleChange(e);
                                          handleAddressChanged();
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.address.addressline1"))}
                                        helperText={getIn(errors, "shipping.address.addressline1")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.address.addressline2"
                                        name="shipping.address.addressline2"
                                        label="Address Line 2"
                                        value={values.shipping.address.addressline2}
                                        onChange={(e) => {
                                          handleChange(e);
                                          handleAddressChanged();
                                        }}
                                        onBlur={handleBlur}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.address.locality1"
                                        name="shipping.address.locality1"
                                        label="City"
                                        value={values.shipping.address.locality1}
                                        onChange={(e) => {
                                          handleChange(e);
                                          handleAddressChanged();
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.address.locality1"))}
                                        helperText={getIn(errors, "shipping.address.locality1")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={8}
                                      md={6}
                                    >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "shipping.address.region1"))}>
                                        <InputLabel id="state-label" shrink>State</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="state-label"
                                          id="shipping.address.region1"
                                          name="shipping.address.region1"
                                          value={values.shipping.address.region1}
                                          onChange={(e) => {
                                            handleChange(e);
                                            handleAddressChanged();
                                          }}
                                          label="State"
                                        >
                                          <option value=""></option>
                                          {states.map((state, index) => (
                                            <option value={state.code} key={index}>
                                              {state.code}-{state.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "shipping.address.region1")}</FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={4}
                                    >
                                      <TextField
                                        disabled={readonly}
                                        id="shipping.address.postalCode"
                                        name="shipping.address.postalCode"
                                        label="Postal Code"
                                        value={values.shipping.address.postalCode}
                                        onChange={(e) => {
                                          handleChange(e);
                                          handleAddressChanged();
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(getIn(errors, "shipping.address.postalCode"))}
                                        helperText={getIn(errors, "shipping.address.postalCode")}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      md={6}
                                    >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "shipping.address.country"))}>
                                        <InputLabel id="country-label" shrink>Country</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="country-label"
                                          id="shipping.address.country"
                                          name="shipping.address.country"
                                          value={values.shipping.address.country}
                                          onChange={(e) => {
                                            handleChange(e);
                                            handleCountryChanged(e);
                                            handleAddressChanged();
                                          }}
                                          label="Country"
                                        >
                                          <option value=""></option>
                                          {referenceLookups.countries.map((country, index) => (
                                            <option value={country.code} key={index}>
                                              {country.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "shipping.address.country")}</FormHelperText>
                                      </FormControl>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                            >
                              <Box className={globalStyles.topic}>
                                <Grid container>
                                  <Grid
                                    item
                                    xs={7}
                                  >
                                    Order Information
                                  </Grid>
                                  <Grid
                                    item
                                    xs={5}
                                  >
                                    {!readonly ? (
                                      <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        width={1}
                                      >
                                        {!associateDeviceClicked && values.order.orderTypeId === 0 ? (
                                          <Tooltip arrow title="Add Supporting Items">
                                            <IconButton onClick={getSKUsAssociatedItems}>
                                              <AddtionalItemsIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : null}

                                        {getRecentlyUsedSKUs().length > 0 ? (
                                          <>
                                            <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                            <Tooltip arrow title="Recently Used SKUs">
                                              <IconButton
                                                color="secondary"
                                                aria-controls="recently-used-menu"
                                                aria-haspopup="true"
                                                onClick={handleAnchorElRecentlyUsedSkusMenuClick}
                                              >
                                                <HistoryIcon />
                                              </IconButton>
                                            </Tooltip>

                                            <Menu
                                              elevation={2}
                                              id="recently-used-menu"
                                              className={globalStyles.rightsideMenu}
                                              anchorEl={anchorElForRecentlyUsedSkusMenu}
                                              keepMounted
                                              open={Boolean(anchorElForRecentlyUsedSkusMenu)}
                                              onClose={handleAnchorElOptionsRecentlyUsedSkusClose}
                                            >
                                              <List>
                                                {getRecentlyUsedSKUs().map((recentlyUsedSKU, index) => (
                                                  <ListItem
                                                    button
                                                    key={index}
                                                    onClick={() => {
                                                      addFromRecentlyUsed(recentlyUsedSKU.sku, recentlyUsedSKU.name);
                                                    }}
                                                  >
                                                    <ListItemIcon>{recentlyUsedSKU.category.toLowerCase() === "*associations" ? <AddtionalItemsIcon fontSize="small" /> : recentlyUsedSKU.category.toLowerCase() === "*shortcut" ? <ShortcutIcon fontSize="small" /> : <RecentlyUsedTagIcon fontSize="small" />}</ListItemIcon>
                                                    <ListItemText
                                                      primary={recentlyUsedSKU.sku}
                                                      secondary={recentlyUsedSKU.name}
                                                    />
                                                  </ListItem>
                                                ))}
                                              </List>
                                            </Menu>
                                          </>
                                        ) : null}

                                        <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                        <Tooltip arrow title="Add New SKUs By Program">
                                          <IconButton onClick={handleAnchorElAddSKUMenuClick}>
                                            <AddIcon color="secondary" />
                                          </IconButton>
                                        </Tooltip>

                                        <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>

                                        <Menu
                                          id="associate-entity-menu"
                                          anchorEl={anchorElForAddSKUMenu}
                                          keepMounted
                                          open={Boolean(anchorElForAddSKUMenu)}
                                          onClose={handleAnchorElAddSKUMenuClose}
                                          getContentAnchorEl={null}
                                          anchorOrigin={{
                                            vertical: "bottom",
                                            horizontal: "center",
                                          }}
                                          transformOrigin={{
                                            vertical: "top",
                                            horizontal: "center",
                                          }}
                                        >
                                          <MenuItem
                                            onClick={() => {
                                              handleAddSKU(0);
                                              handleAnchorElAddSKUMenuClose();
                                            }}
                                          >
                                            <ListItemIcon>
                                              <AddIcon fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit">SKUs By Program</Typography>
                                          </MenuItem>
                                          <MenuItem
                                            onClick={() => {
                                              handleAddSKU(1);
                                              handleAnchorElAddSKUMenuClose();
                                            }}
                                          >
                                            <ListItemIcon>
                                              <AddIcon fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit">All SKUs</Typography>
                                          </MenuItem>
                                        </Menu>

                                        {values.order.orderTypeId <= 0 && orderItems.length > 0 && (
                                          <Tooltip arrow title="Clear SKUs">
                                            <IconButton
                                              onClick={(e) => {
                                                openConfirmationDialog("clearcartsku");
                                              }}
                                            >
                                              <ClearAllIcon color="error" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ) : null}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>

                            {!readonly ? (
                              <Grid item xs={12}>
                                {showOrderSkusItemQuantity() > 0 
                                && values.order?.hasSidewalk === true 
                                && values.order?.sidewalkValidationResult === false 
                                && values.order?.overrideSidewalkValidation === false ? (
                                  <Alert severity="error">
                                  Address has poor or no sidewalk service, confirm the account has a gateway or override to continue<br/><br/>
                                  <Button className={globalStyles.buttonDefault} variant="contained" size="small" onClick={() => { overrideSidewalk(); }}>
                                    Override Validation
                                  </Button>
                                  </Alert>
                                ) : null }

                                {showOrderSkusItemQuantity() > 0 
                                  && values.order?.hasSidewalk === true 
                                  && values.order?.sidewalkValidationResult === false 
                                  && values.order?.overrideSidewalkValidation === true ? (
                                  <Alert 
                                    severity="success"
                                    action={
                                      <Button color="inherit" size="small" onClick={() => { undoOverrideSidewalk(); }}>
                                        Undo Override
                                      </Button>
                                    }
                                  >
                                    Sidewalk Validation Overridden
                                  </Alert>
                                ) : null}                      

                                {/* Indicates no check needed when hasSidewalk is false */}
                                {/* {showOrderSkusItemQuantity() > 0 && values.order?.hasSidewalk === false ? (
                                  <Alert severity="info">
                                    Sidewalk validation skipped: No Sidewalk-enabled devices are currently selected in this order.
                                  </Alert>
                                ) : null} */}

                                {alertDataErrors.length > 0 ? (
                                  <>
                                    <Alert severity="error">
                                      {alertDataErrors.map((message, index) => (
                                        <Box key={index} style={{ marginBottom: (alertDataErrors.length - 1) === index ? '0px' : '15px' }}>
                                          {alertDataErrors.length > 1 ? <>{index + 1})&nbsp;</> : null}
                                          {message}
                                        </Box>
                                      ))}
                                    </Alert>
                                    <Box p={1}></Box>
                                  </>
                                ) : null}

                                {alertDataWarnings.length > 0 ? (
                                  <>
                                    <Alert severity="warning">
                                      {alertDataWarnings.map((message, index) => (
                                        <Box key={index} style={{ marginBottom: (alertDataWarnings.length - 1) === index ? '0px' : '15px' }}>
                                          {alertDataWarnings.length > 1 ? <>{index + 1})&nbsp;</> : null}
                                          {message}
                                        </Box>
                                      ))}
                                    </Alert>
                                    <Box p={2}></Box>
                                  </>
                                ) : null}
                              </Grid>
                            ) : null}

                            {values.order.installOption === 'vgi' || values.order.installOption === 'pro' ? (
                              <Grid item xs={12}>
                                <Alert severity="warning" > Please submit a CRM case for the {values.order.installOption.toUpperCase()} installation appointment.</Alert>
                              </Grid>
                            ) : null}

                            <Grid item xs={12} md={12} lg={4}>
                              <Grid container spacing={3} style={{ borderRight: '2px solid #eaeaea' }}>
                                <Grid item xs={12}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.fulfillmentVendorId"))}>
                                        <InputLabel id="fulfillmentVendorId-label" shrink>Fulfillment Vendor</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="fulfillmentVendorId-label"
                                          id="order.fulfillmentVendorId"
                                          name="order.fulfillmentVendorId"
                                          value={values.order.fulfillmentVendorId}
                                          onChange={handleChange}
                                        >
                                          <option value={-1}></option>
                                          {fulfillmentVendors.map((lookup, index) => (
                                            <option value={lookup.value} key={index}>
                                              {lookup.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.fulfillmentVendorId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.platformVendorId"))}>
                                        <InputLabel id="platformVendorId-label" shrink>Platform Vendor</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="platformVendorId-label"
                                          id="order.platformVendorId"
                                          name="order.platformVendorId"
                                          value={values.order.platformVendorId}
                                          onChange={handleChange}
                                        >
                                          <option value={-1}></option>
                                          {platformVendors.map((lookup, index) => (
                                            <option value={lookup.value} key={index}>
                                              {lookup.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.platformVendorId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.deviceOwnershipId"))}>
                                        <InputLabel id="device-ownership-id-label" shrink>Device Ownership</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="device-ownership-id-label"
                                          id="order.deviceOwnershipId"
                                          name="order.deviceOwnershipId"
                                          value={values.order.deviceOwnershipId}
                                          onChange={handleChange}
                                        >
                                          <option value={-1}></option>
                                          {deviceOwnerships.map((lookup, index) => (
                                            <option value={lookup.value} key={index}>
                                              {lookup.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.deviceOwnershipId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.chargeableTypeId"))}>
                                        <InputLabel id="chargeable-type-id-label" shrink>Chargeable Type</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="chargeable-type-id-label"
                                          id="order.chargeableTypeId"
                                          name="order.chargeableTypeId"
                                          value={values.order.chargeableTypeId}
                                          onChange={(e) => {
                                            handleChange(e);
                                            getProgramOrderContractOrders(Number(e.currentTarget.value));
                                          }}
                                        >
                                          <option value={-1}></option>
                                          {chargeableTypes.map((lookup, index) => (
                                            <option value={lookup.value} key={index}>
                                              {lookup.name}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.chargeableTypeId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.orderTypeId"))}>
                                        <InputLabel id="order-type-id-label" shrink>Order Type</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="order-type-id-label"
                                          id="order.orderTypeId"
                                          name="order.orderTypeId"
                                          value={values.order.orderTypeId}
                                          onChange={(e: any) => {
                                            handleChange(e);
                                            orderTypeChanged(e.currentTarget.value as number);
                                          }}
                                        >
                                          {orderTypes.map((lookup, index) => (
                                            <option value={lookup.value} key={index}>
                                              {lookup.name}
                                              {lookup.value > 0 ? ` - ${lookup.value}` : ""}
                                            </option>
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.orderTypeId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={6} >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.installOption"))}>
                                        <InputLabel id="installOption-label" shrink>Install Option</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="installOption-label"
                                          id="order.installOption"
                                          name="order.installOption"
                                          value={values.order.installOption}
                                          onChange={handleChange}
                                        >
                                          <option value={""}></option>
                                          <option value={"pro"}>PRO</option>
                                          <option value={"self"}>Self</option>
                                          <option value={"vgi"}>VGI</option>
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.installOption")}</FormHelperText>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={12} >
                                      <FormControl fullWidth={true}>
                                        <InputLabel shrink>CRM Case #</InputLabel>
                                        <Input
                                          disabled={readonly}
                                          required
                                          id="order.crmCaseNumber"
                                          name="order.crmCaseNumber"
                                          value={values.order.crmCaseNumber}
                                          onChange={handleChange}
                                          endAdornment={
                                            <InputAdornment position="end">
                                              <React.Fragment>
                                                {!readonly && values.order.crmCaseNumber !== "" ? (
                                                  <Tooltip arrow title="CRM Case #">
                                                    <IconButton
                                                      className={pageStyles.dropdownButton}
                                                      onClick={() => {
                                                        openCRMUrl(values.order.crmCaseNumber);
                                                      }}
                                                    >
                                                      <OpenInNewIcon color="secondary" />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : null}

                                                <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>

                                                {!readonly && crmCases.length > 0 ? (
                                                  <Tooltip arrow title="Select CRM Case #">
                                                    <IconButton
                                                      className={pageStyles.dropdownButton}
                                                      onClick={handleSelectCrmCases}
                                                    >
                                                      <LookUpArrowIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : null}
                                              </React.Fragment>
                                            </InputAdornment>
                                          }
                                        />
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4} lg={12}  >
                                      <FormControl fullWidth error={Boolean(getIn(errors, "order.contractOrderId"))}>
                                        <InputLabel id="order-type-id-label" shrink>Contract Order Id</InputLabel>
                                        <Select
                                          disabled={readonly}
                                          native
                                          labelId="contractOrderId-label"
                                          id="order.contractOrderId"
                                          name="order.contractOrderId"
                                          value={values.order.contractOrderId}
                                          onChange={(e) => {
                                            handleChange(e);
                                            getOrderContractOrderDetail(Number(e.currentTarget.value));
                                          }}
                                        >
                                          <option value={-1}> </option>
                                          {contracts.map((contract, index) => (
                                            contract.contractOrderId > 0 ? (
                                              <option value={contract.contractOrderId} key={index}>
                                                {contract.contractOrderNumber}
                                              </option>
                                            ) : (
                                              <option value={contract.contractOrderId} key={index} style={{ color: "orange" }}>
                                                {contract.contractOrderNumber}
                                              </option>
                                            )
                                          ))}
                                        </Select>
                                        <FormHelperText error>{getIn(errors, "order.contractOrderId")}</FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    {contractOrder !== null ? (
                                      <>
                                        <Grid item xs={12}  >
                                          {contractOrder.statusId === 2 ? (
                                            <TextField disabled={true} label="Status" value={contractOrder.status} fullWidth={true} />
                                          ) : (
                                            <TextField disabled={true} label="Status" value={contractOrder.status} fullWidth={true} className={pageStyles.statusPause} />
                                          )}
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Equipment Vendor" value={contractOrder.equipmentVendor} fullWidth={true} />
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Posting Status" value={contractOrder.postingStatus} fullWidth={true} />
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Contract Type" value={contractOrder.contractType} fullWidth={true} />
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Invoice Status" value={contractOrder.invoiceStatus} fullWidth={true} />
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Effective Date" value={formatDateForInput(contractOrder.effectiveDate)} fullWidth={true} />
                                        </Grid>
                                        <Grid item xs={6}  >
                                          <TextField disabled={true} label="Expiration Date" value={formatDateForInput(contractOrder.expirationDate)} fullWidth={true} />
                                        </Grid>
                                      </>
                                    ) : null}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Box p={2}>&nbsp;</Box>
                            </Grid>

                            <Grid item xs={12} md={12} lg={8} style={{ paddingTop: 0 }}>
                              <Box className={pageStyles.datagridContainerForOrderItems}>
                                <CustomDataGrid
                                  uniqueGridCode={"OrderItemsResult"}
                                  columns={columnsForOrderItems}
                                  rows={orderItems}
                                  showToolBar={false}
                                  sortModel={[
                                    { field: "skuType", sort: "asc" },
                                    { field: "name", sort: "asc" },
                                  ]}
                                  handleDataGridClick={handleDataGridClickForOrderItems}
                                />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={7} md={6} >
                              <Box
                                p={1}
                                display="flex"
                                justifyItems="center"
                                alignItems="center"
                                style={{ height: "80px" }}
                              >
                                <Typography variant="subtitle1"> {`Total Item(s) : ${orderItems.length}, Quantities : ${showOrderSkusItemQuantity()}`}</Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={5} md={6} >
                              <Box
                                p={3}
                                display="flex"
                                justifyContent="right"
                                alignContent="center"
                              >
                                {((!readonly && !saveInProgress) || overrideDelete) ? (
                                  <>
                                    {isDeleteOrderButtonVisible() ? (
                                      <Tooltip title="Delete this order draft">
                                        <Button
                                          className={globalStyles.errorText}
                                          onClick={() => {
                                            openConfirmationDialog("canceldraftorder");
                                          }}
                                        >
                                          <DeleteIcon />
                                        </Button>
                                      </Tooltip>
                                    ) : null}
                                  </>
                                ) : null}
                                {(!readonly && !saveInProgress) ? (
                                  <>
                                    {isCloneOrderButtonVisible(0) ? (
                                      <Tooltip title="Clone this order and save it as a new draft">
                                        <Button
                                          variant="contained"
                                          color="default"
                                          className={globalStyles.buttonDefault}
                                          onClick={() => {
                                            openConfirmationDialog("clonedraftorder");
                                          }}
                                        >
                                          Clone
                                        </Button>
                                      </Tooltip>
                                    ) : null}

                                    {isSaveOrderButtonVisible() ? (
                                      <Tooltip title={isNew ? "Save this order draft" : "Update this order draft"}>
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          className={globalStyles.buttonDefault}
                                          onClick={() => {
                                            validateSaveDraft();
                                          }}
                                        >
                                          {isNew ? "Save" : "Update"}
                                        </Button>
                                      </Tooltip>
                                    ) : null}

                                    {isApproveOrderButtonVisible() && showSidewalkValidationError() === false ? (
                                      <Tooltip title="Approve this order">
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          className={globalStyles.buttonDefault}
                                          onClick={() => {
                                            openConfirmationDialog("approvedraftorder");
                                          }}
                                        >
                                          Approve
                                        </Button>
                                      </Tooltip>
                                    ) : null}
                                  </>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <Box p={3}></Box>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    datagridContainerForOrderItems: {
      height: "600px",
      width: "100%",
      [theme.breakpoints.down("xs")]: {
        height: "400px",
      },
      "& .MuiDataGrid-root": {
        borderColor: "transparent !important",
      },
      "& .MuiDataGrid-root .MuiDataGrid-toolbarContainer": {
        display: 'none'
      }
    },
    recentlyUsedContainer: {
      height: "400px",
      width: "100%",
      overflow: "auto",
    },
    datagridContainerForFolderSearch: {
      height: "calc(100vh - 380px)",
      width: "100%",
      [theme.breakpoints.down("xs")]: {
        height: "400px",
      },
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    listItemIcon: {
      justifyContent: "flex-end",
    },
    dropdownButton: {
      padding: 0,
      transition: "",
    },
    statusPause: {
      "& .MuiInputBase-input.Mui-disabled": {
        color: theme.palette.error.main,
        fontWeight: 'bolder',
        animationName: "$blinker",
        animationDuration: "2s",
        animationTransition: "linear",
        animationIterationCount: "infinite",
      }
    },
    "@keyframes blinker": {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
  })
);

export default ManualOrderCRUD;